/**
 * Offcanvas navigation drill-down (tablet & mobile mega menu), plus the
 * compact top-bar tweak that moves the search toggle next to the hamburger
 * on tablet/mobile.
 *
 * Opening/closing the offcanvas panel itself is handled by the parent
 * theme's global "ss" component JS (data-ss-toggle="offcanvas"). This module
 * only drives the internal mega-menu drill-down: tapping a parent item
 * slides its <ul class="sub-menu"> in as a full panel, the back button
 * steps back out, and the panel resets whenever the offcanvas is reopened.
 */

// Panel-panel offcanvas yang dibuka tepat di bawah header (bukan menutupinya
// dari atas layar) - navigasi (hamburger) dan search sama-sama dapat
// perlakuan ini, lihat initOffcanvasHeaderOffset/initOffcanvasHeaderStacking.
// Keduanya juga saling menutup satu sama lain, lihat
// initOffcanvasMutualExclusion - cuma satu yang boleh terbuka sekaligus,
// karena keduanya berbagi ruang yang sama persis di bawah header.
const HEADER_OFFCANVAS_SELECTORS = [".dufeng-offcanvas-nav", ".dufeng-offcanvas-search"];

export function initSiteNavigation() {
  initCompactSearchPlacement();
  initOffcanvasDrilldown();
  initOffcanvasHeaderOffset(HEADER_OFFCANVAS_SELECTORS);
  initOffcanvasAdminBarOffset(HEADER_OFFCANVAS_SELECTORS);
  initOffcanvasHeaderStacking(HEADER_OFFCANVAS_SELECTORS);
  initOffcanvasMutualExclusion(HEADER_OFFCANVAS_SELECTORS);
  initOffcanvasToggleClose("#site-navigation-toggler", "#site-offcanvas-navigation");
  initOffcanvasToggleClose(".nav-action-search .ss-dropdown-toggle", "#site-offcanvas-search");
  initMegaMenu();
  initStickyBodyState();
}

/**
 * Sembunyikan satu panel offcanvas ("ss" core, blocks/block-offcanvas-*.php)
 * secara programatik. Logikanya disalin persis dari hide() milik "ss" core
 * (durasi transisi 300ms termasuk) karena core tidak mengekspos fungsi
 * hide()-nya untuk dipanggil dari luar - dipakai di initOffcanvasToggleClose
 * (toggler dobel jadi trigger close) dan initOffcanvasMutualExclusion
 * (nav & search saling menutup satu sama lain).
 */
function hideOffcanvasPanel(offcanvas) {
  const backdrop = document.querySelector(".ss-offcanvas-backdrop");
  if (backdrop) {
    backdrop.classList.remove("ss-show");
    window.setTimeout(() => backdrop.remove(), 300);
  }
  offcanvas.classList.add("ss-hiding");
  offcanvas.classList.remove("ss-show");
  window.setTimeout(() => {
    offcanvas.classList.remove("ss-hiding");
    offcanvas.style.visibility = "hidden";
    offcanvas.dispatchEvent(new CustomEvent("hidden.ss.offcanvas"));
  }, 300);
}

/**
 * Navigasi & search sama-sama offcanvas yang dibuka tepat di bawah header
 * (posisi & lebarnya bisa tumpang tindih di layar sempit) - "ss" core tidak
 * tahu-menahu soal panel lain begitu satu offcanvas dibuka, jadi kalau user
 * buka yang satu selagi yang lain masih terbuka, keduanya bisa numpuk.
 * Begitu salah satu terbuka, tutup semua panel LAIN dalam daftar yang masih
 * kebuka.
 */
function initOffcanvasMutualExclusion(offcanvasSelectors) {
  const offcanvases = offcanvasSelectors.map((selector) => document.querySelector(selector)).filter(Boolean);
  if (offcanvases.length < 2) return;

  offcanvases.forEach((offcanvas) => {
    offcanvas.addEventListener("shown.ss.offcanvas", () => {
      offcanvases.forEach((other) => {
        if (other !== offcanvas && other.classList.contains("ss-show")) {
          hideOffcanvasPanel(other);
        }
      });
    });
  });
}

/**
 * Cerminkan status sticky navbar ke <body> sebagai
 * .site-navigation-sticky-activated.
 *
 * Parent theme menambah/melepas .site-navigation-sticky-active langsung di
 * <nav> lewat classList (assets/js/script.min.js) tanpa memicu event apa pun,
 * jadi satu-satunya cara mengikutinya adalah mengamati atribut class-nya.
 *
 * Penandanya ditaruh di <body> supaya elemen mana pun di halaman - termasuk
 * yang dicetak di wp_footer, jauh di luar struktur header - bisa bereaksi
 * lewat CSS tanpa perlu selector yang menembus header.
 */
function initStickyBodyState() {
  const nav = document.querySelector(".site-navigation.site-navigation-sticky");
  if (!nav) return;

  const sync = () => {
    document.body.classList.toggle(
      "site-navigation-sticky-activated",
      nav.classList.contains("site-navigation-sticky-active")
    );
  };

  new MutationObserver(sync).observe(nav, {
    attributes: true,
    attributeFilter: ["class"],
  });

  // Sticky bisa saja sudah aktif sebelum modul ini jalan (mis. halaman dimuat
  // dalam keadaan ter-scroll).
  sync();
}

/**
 * Mega menu desktop. Buka/tutup sebenarnya ditangani CSS (:hover /
 * :focus-within di component/_mega-menu.scss) supaya tetap jalan tanpa JS;
 * modul ini hanya menambah:
 * - jeda hover-intent, supaya panel tidak berkedip saat kursor cuma menyapu menu
 * - aria-expanded pada trigger
 * - Escape untuk menutup panel dan mengembalikan fokus ke trigger
 *
 * Markup dari includes/nav-mega-menu.php.
 */
function initMegaMenu() {
  const items = document.querySelectorAll(".site-navigation-menu .dufeng-has-mega");
  if (!items.length) return;

  const nav = document.querySelector(".site-navigation");
  const desktop = window.matchMedia("(min-width: 992px)");
  const OPEN_DELAY = 90;
  const CLOSE_DELAY = 160;

  // Mematikan fallback :hover murni di CSS; mulai sekarang JS yang menentukan
  // kapan panel terbuka (lihat component/_mega-menu.scss).
  document.documentElement.classList.add("has-mega-js");

  // Penanda di <nav>, bukan cuma di <li>: elemen lain di header (mis. logo,
  // action, atau backdrop) bisa ikut bereaksi lewat CSS tanpa perlu tahu item
  // mana yang sedang terbuka. Dihitung ulang dari state semua item supaya
  // pindah hover antar item tidak sempat mematikan penanda.
  const syncNavState = () => {
    const anyOpen = Array.from(items).some((el) => el.classList.contains("is-mega-open"));
    nav?.classList.toggle("site-navigation-mega-active", anyOpen);
  };

  items.forEach((item) => {
    const trigger = item.querySelector(".dufeng-mega-trigger");
    let timer = null;

    const setOpen = (open) => {
      item.classList.toggle("is-mega-open", open);
      trigger?.setAttribute("aria-expanded", open ? "true" : "false");
      syncNavState();
    };

    const schedule = (open) => {
      if (!desktop.matches) return;
      window.clearTimeout(timer);
      timer = window.setTimeout(() => setOpen(open), open ? OPEN_DELAY : CLOSE_DELAY);
    };

    item.addEventListener("mouseenter", () => schedule(true));
    item.addEventListener("mouseleave", () => schedule(false));
    item.addEventListener("focusin", () => {
      window.clearTimeout(timer);
      setOpen(true);
    });
    item.addEventListener("focusout", (e) => {
      if (!item.contains(e.relatedTarget)) setOpen(false);
    });
    item.addEventListener("keydown", (e) => {
      if (e.key !== "Escape") return;
      window.clearTimeout(timer);
      setOpen(false);
      trigger?.focus();
    });
  });

  // Kembali ke mobile: pastikan tidak ada panel yang tertinggal terbuka.
  desktop.addEventListener("change", (e) => {
    if (e.matches) return;
    items.forEach((item) => {
      item.classList.remove("is-mega-open");
      item.querySelector(".dufeng-mega-trigger")?.setAttribute("aria-expanded", "false");
    });
    syncNavState();
  });
}

/**
 * .ss-navbar-left / .ss-navbar-center / .ss-navbar-right are separate grid
 * cells, so CSS `order` alone can't move the search action across them.
 * Below 992px it's relocated (same node) to sit right after the menu
 * toggler; at 992px+ it moves back to its original spot at the start of the
 * right-hand action group.
 *
 * The trigger's behavior is swapped along with its position: desktop opens
 * the mega-style .ss-dropdown-menu in place (see _header.scss), while
 * mobile/tablet opens the dedicated #site-offcanvas-search panel instead
 * (blocks/block-offcanvas-search.php) so search gets a full offcanvas with
 * its own header/close button rather than a cramped dropdown box. The `ss`
 * component JS reads data-ss-toggle/data-ss-target at click time, so
 * swapping them ahead of time here is enough - no rebind needed.
 */
function initCompactSearchPlacement() {
  const searchAction = document.querySelector(".nav-action-search");
  const toggler = document.querySelector(".site-navigation-toggler");
  const navRight = document.querySelector(".ss-navbar-right");
  const trigger = searchAction?.querySelector(".ss-dropdown-toggle");
  if (!searchAction || !toggler || !navRight || !trigger) return;

  const mql = window.matchMedia("(max-width: 991px)");

  function place(isCompact) {
    if (isCompact) {
      toggler.after(searchAction);
      trigger.setAttribute("data-ss-toggle", "offcanvas");
      trigger.setAttribute("data-ss-target", "#site-offcanvas-search");
    } else {
      navRight.prepend(searchAction);
      trigger.setAttribute("data-ss-toggle", "dropdown");
      trigger.removeAttribute("data-ss-target");
    }
  }

  place(mql.matches);
  mql.addEventListener("change", (e) => place(e.matches));
}

function initOffcanvasDrilldown() {
  const offcanvas = document.querySelector(".dufeng-offcanvas-nav");
  if (!offcanvas) return;

  const body = offcanvas.querySelector(".dufeng-offcanvas-nav__body");
  const panelsWrap = offcanvas.querySelector(".dufeng-offcanvas-nav__panels");
  const topLevelList = offcanvas.querySelector(".dufeng-offcanvas-nav-menu");
  const backButton = offcanvas.querySelector(".dufeng-offcanvas-nav__back");
  const backTitle = offcanvas.querySelector(".dufeng-offcanvas-nav__back-title");

  if (!body || !panelsWrap || !topLevelList) return;

  const panels = [topLevelList, ...panelsWrap.querySelectorAll(".sub-menu")];
  const stack = [];

  // Setiap level nested <ul class="sub-menu"> dirender position:absolute DI
  // DALAM <li> induknya, jadi containing block-nya adalah panel level di
  // atasnya (yang juga position:absolute) - begitu level itu di-".is-passed"
  // (translateX(-100%)), level di bawahnya ikut ke-offset lewat containing
  // block yang sama, walau sudah ditandai ".is-open" sendiri (net translateX
  // tetap -100%, jadi kelihatan kosong/di luar layar). Snapshot referensi tiap
  // submenu SEBELUM ada interaksi, lalu pindahkan (appendChild) ke panelsWrap
  // begitu dibuka: hasilnya submenu manapun yang aktif selalu jadi anak
  // langsung panelsWrap, sejajar topLevelList, jadi containing block-nya sama
  // persis (.dufeng-offcanvas-nav__panels) tidak peduli separah apa nesting
  // aslinya di menu.
  const submenuByItem = new Map();
  panelsWrap.querySelectorAll("li.menu-item-has-children").forEach((li) => {
    const submenu = li.querySelector(":scope > .sub-menu");
    if (submenu) submenuByItem.set(li, submenu);
  });

  function render() {
    panels.forEach((panel) => panel.classList.remove("is-open", "is-passed"));

    if (!stack.length) {
      topLevelList.classList.add("is-open");
    } else {
      topLevelList.classList.add("is-passed");
      stack.forEach(({ submenu }, index) => {
        submenu.classList.add(index === stack.length - 1 ? "is-open" : "is-passed");
      });
    }

    offcanvas.classList.toggle("is-drilled", stack.length > 0);
    if (backTitle) {
      backTitle.textContent = stack.length ? stack[stack.length - 1].label : "";
    }
    body.scrollTop = 0;
  }

  function openSubmenu(li) {
    const submenu = submenuByItem.get(li);
    if (!submenu) return;

    if (submenu.parentElement !== panelsWrap) {
      panelsWrap.appendChild(submenu);
    }

    const label = li.querySelector(":scope > a")?.textContent.trim() || "";
    stack.push({ submenu, label });
    render();
  }

  function goBack() {
    if (!stack.length) return;
    stack.pop();
    render();
  }

  function reset() {
    if (!stack.length) return;
    stack.length = 0;
    render();
  }

  // Delegasi di panelsWrap, bukan topLevelList: begitu sebuah submenu dipindah
  // ke panelsWrap (lihat openSubmenu), ia bukan lagi descendant topLevelList,
  // jadi listener di sana tidak akan lagi menangkap klik di level 3+.
  panelsWrap.addEventListener("click", (e) => {
    const link = e.target.closest("a");
    if (!link) return;

    const li = link.closest("li.menu-item-has-children");
    if (!li || link.parentElement !== li) return;

    e.preventDefault();
    openSubmenu(li);
  });

  backButton?.addEventListener("click", goBack);

  // "ss" (parent theme) dispatch shown/hidden.ss.offcanvas langsung di elemen
  // offcanvas-nya tanpa {bubbles: true}, jadi listener-nya harus dipasang di
  // elemen itu sendiri - dipasang di document, event-nya tidak akan pernah
  // sampai.
  offcanvas.addEventListener("shown.ss.offcanvas", reset);
  offcanvas.addEventListener("hidden.ss.offcanvas", reset);

  render();
}

/**
 * Panel-panel offcanvas (navigasi & search) dibuka tepat di bawah header
 * (bukan menutupinya dari atas layar, lihat top: var(--dufeng-navbar-offset)
 * di component/_offcanvas-navigation.scss). Tinggi header yang kelihatan
 * beda-beda: penuh dengan topbar promo saat scroll 0, atau cuma navbar
 * begitu header jadi sticky - jadi offset-nya diukur langsung dari posisi
 * navbar di viewport (getBoundingClientRect().bottom mencakup topbar di
 * atasnya secara alami kalau masih ikut ter-scroll bersama), bukan
 * dihardcode.
 *
 * Listener scroll/resize cuma dipasang selama salah satu panel-nya terbuka
 * supaya tidak ada biaya tambahan waktu browsing normal.
 *
 * "shown"/"hidden" tiap panel di-treat sebagai sinyal "cek ulang", BUKAN
 * "pasang"/"lepas" langsung - initOffcanvasMutualExclusion menutup panel
 * LAIN secara programatik lewat hideOffcanvasPanel() begitu satu panel
 * dibuka, dan hidden.ss.offcanvas panel yang ditutup paksa itu baru
 * benar-benar terpicu ~300ms kemudian (durasi transisi core). Kalau
 * listener-nya cuma add/remove sepihak per panel, hidden panel yang
 * ditutup-paksa itu bisa nyusul melepas listener scroll/resize padahal
 * panel LAIN barusan dibuka & masih butuh itu tetap aktif.
 */
function initOffcanvasHeaderOffset(offcanvasSelectors) {
  const nav = document.querySelector(".site-navigation.site-navigation-sticky");
  if (!nav) return;

  const offcanvases = offcanvasSelectors.map((selector) => document.querySelector(selector)).filter(Boolean);
  if (!offcanvases.length) return;

  const root = document.documentElement;
  let ticking = false;

  const sync = () => {
    const offset = Math.max(0, nav.getBoundingClientRect().bottom);
    root.style.setProperty("--dufeng-navbar-offset", `${offset}px`);
  };

  const onViewportChange = () => {
    if (ticking) return;
    ticking = true;
    window.requestAnimationFrame(() => {
      sync();
      ticking = false;
    });
  };

  const syncActiveListener = () => {
    const anyOpen = offcanvases.some((el) => el.classList.contains("ss-show"));
    if (anyOpen) {
      sync();
      window.addEventListener("scroll", onViewportChange, { passive: true });
      window.addEventListener("resize", onViewportChange);
    } else {
      window.removeEventListener("scroll", onViewportChange);
      window.removeEventListener("resize", onViewportChange);
    }
  };

  offcanvases.forEach((offcanvas) => {
    // Lihat catatan di initOffcanvasDrilldown: listener harus di elemen
    // offcanvas-nya sendiri, bukan document, karena eventnya tidak bubble.
    offcanvas.addEventListener("shown.ss.offcanvas", syncActiveListener);
    offcanvas.addEventListener("hidden.ss.offcanvas", syncActiveListener);
  });
}

/**
 * "ss" core (parent theme, assets/js/laristurbo-core.min.js) menempelkan
 * inline margin-top setinggi #wpadminbar ke SEMUA .ss-offcanvas-start /
 * .ss-offcanvas-end begitu DOMContentLoaded. Masuk akal untuk offcanvas
 * bawaan yang top-nya 0 (supaya tidak ketutup admin bar), tapi salah untuk
 * dua panel ini: top-nya sudah diukur dari posisi navbar di viewport lewat
 * initOffcanvasHeaderOffset(), angka yang secara alami SUDAH termasuk tinggi
 * admin bar. Dibiarkan, admin bar kehitung dua kali dan panel melorot ~46px
 * dari bawah header - gap yang cuma muncul waktu user login (admin bar cuma
 * ada di situ), sementara pengunjung biasa aman.
 *
 * Inline style tidak bisa dikalahkan CSS tanpa !important, jadi dinolkan dari
 * sini: sekali di init, lalu diulang tiap panel dibuka - urutan handler
 * DOMContentLoaded antara core & bundle ini tidak dijamin, jadi init saja
 * bisa keburu ditimpa core.
 */
function initOffcanvasAdminBarOffset(offcanvasSelectors) {
  const offcanvases = offcanvasSelectors.map((selector) => document.querySelector(selector)).filter(Boolean);
  if (!offcanvases.length) return;

  const clear = () => offcanvases.forEach((offcanvas) => (offcanvas.style.marginTop = "0px"));

  clear();
  offcanvases.forEach((offcanvas) => offcanvas.addEventListener("shown.ss.offcanvas", clear));
}

/**
 * Backdrop offcanvas ("ss" core) menutupi seluruh viewport dengan z-index
 * lebih tinggi dari header - begitu salah satu panel ini terbuka, header
 * (dan tombolnya, satu-satunya trigger close sejak logo/close di-takeout)
 * jadi ketutup backdrop & tidak bisa diklik. Lihat
 * body.dufeng-offcanvas-panel-open di component/_offcanvas-navigation.scss
 * untuk aturan z-index-nya (dan section/_header.scss untuk override warna
 * header homepage yang sama-sama dipicu class ini).
 *
 * Sama seperti initOffcanvasHeaderOffset: class body-nya disinkronkan ulang
 * dari status ss-show semua panel di setiap shown/hidden, bukan
 * ditambah/dilepas sepihak per panel - lihat catatan di sana soal hidden
 * panel yang ditutup-paksa oleh initOffcanvasMutualExclusion bisa menyusul
 * ~300ms kemudian dan salah melepas class ini padahal panel LAIN barusan
 * dibuka.
 *
 * Class yang sama ditempel ke <html> juga, bukan cuma <body>: dipakai untuk
 * mengunci scroll halaman selama panel terbuka (overflow:hidden perlu
 * ditulis di kedua elemen, lihat catatannya di
 * component/_offcanvas-navigation.scss).
 */
function initOffcanvasHeaderStacking(offcanvasSelectors) {
  const offcanvases = offcanvasSelectors.map((selector) => document.querySelector(selector)).filter(Boolean);

  const sync = () => {
    const anyOpen = offcanvases.some((el) => el.classList.contains("ss-show"));
    document.body.classList.toggle("dufeng-offcanvas-panel-open", anyOpen);
    document.documentElement.classList.toggle("dufeng-offcanvas-panel-open", anyOpen);
  };

  offcanvases.forEach((offcanvas) => {
    offcanvas.addEventListener("shown.ss.offcanvas", sync);
    offcanvas.addEventListener("hidden.ss.offcanvas", sync);
  });
}

/**
 * Panel navigasi & search tidak lagi punya tombol close sendiri (logo &
 * close di-takeout, lihat blocks/block-offcanvas-navigation.php dan
 * blocks/block-offcanvas-search.php) - tombol yang membukanya di header
 * (togglerSelector) yang jadi satu-satunya trigger, dan sekarang harus
 * berfungsi ganda sebagai close saat panelnya terbuka.
 *
 * Handler klik "ss" (parent theme, data-ss-toggle="offcanvas") selalu
 * memanggil show() apa pun statusnya, tidak toggle - jadi begitu panel
 * sedang terbuka, klik di tombol ini di-intercept di sini dan tidak
 * dibiarkan bubbling ke listener document milik "ss" itu.
 */
function initOffcanvasToggleClose(togglerSelector, offcanvasSelector) {
  const toggler = document.querySelector(togglerSelector);
  const offcanvas = document.querySelector(offcanvasSelector);
  if (!toggler || !offcanvas) return;

  toggler.addEventListener("click", (e) => {
    if (!offcanvas.classList.contains("ss-show")) return;
    e.preventDefault();
    e.stopPropagation();
    hideOffcanvasPanel(offcanvas);
  });

  // Lihat catatan di initOffcanvasDrilldown: listener harus di elemen
  // offcanvas-nya sendiri, bukan document, karena eventnya tidak bubble.
  offcanvas.addEventListener("shown.ss.offcanvas", () => toggler.setAttribute("aria-expanded", "true"));
  offcanvas.addEventListener("hidden.ss.offcanvas", () => toggler.setAttribute("aria-expanded", "false"));
}
